'use client';

import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '@/ui/Input';
import * as ApiAuth from '@/api/auth';
import { toast } from 'sonner';
import React, { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Loader from '@/ui/Loader/Loader';
import { ROOT_PRIORITY_URL } from '@/constants/url';


const schema = z.object({
  email: z.string().email({ message: 'Введите корректный email' }),
  password: z.string().min(6, { message: 'Пароль должен быть минимум 6 символов' }),
});

type FormData = z.infer<typeof schema>;

const StyledButton = styled.button`
  width: 100%;
  padding: 10px;
  background-color: #0d6efd;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background-color: #0b5ed7;
  }
`;

export default function LoginForm() {
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams();

  // Получаем параметры из URL
  const redirectPath = searchParams.get('redirect_path');
  const noReturn = searchParams.get('no_return') || '0';

  // Декодируем redirect_path
  const decodedRedirectPath = redirectPath ? decodeURIComponent(redirectPath) : ROOT_PRIORITY_URL;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);

      await ApiAuth.login({
        email: data.email,
        password: data.password,
      }).then(() => {
        // Если авторизация успешна и no_return=0, перенаправляем
        if (noReturn === '0' && decodedRedirectPath) {
          window.location.href = decodedRedirectPath;
        } else {
          // Перенаправляем пользователя
          window.location.href = `/account?${searchParams.toString()}`;
        }
      });

      reset();
    } catch (error) {
      console.error(error);
      toast.error('Что-то пошло не так. Попробуйте позже');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Input
        type="text"
        placeholder="Email"
        {...register('email')}
        error={errors.email}
      />
      <Input
        type="password"
        placeholder="Пароль"
        {...register('password')}
        error={errors.password}
      />
      <StyledButton type="submit" disabled={loading}>{loading ?
        <Loader size="mini" color="black" speed="fast" /> : 'Войти'}</StyledButton>
    </form>
  );
}
